/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useParams, Navigate, Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import ArticleContent from '../blog/components/ArticleContent';
import BlogNewsletter from '../blog/components/BlogNewsletter';
import ArticleCta from '../blog/components/ArticleCta';
import BackToTop from '../../components/feature/BackToTop';
import YouMightAlsoLike from './components/YouMightAlsoLike';
import { useArticleSchema } from '../../hooks/useStructuredData';
import { useSEOHead } from '../../hooks/useSEOHead';
import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  // Find article synchronously — blogPosts is static data, no async needed.
  const article = blogPosts.find(post => post.slug === slug) ?? null;

  useEffect(() => {
    if (!article) return;

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://rockhoundauto.com/blog',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: `https://rockhoundauto.com/blog/${article.slug}`,
        },
      ],
    };

    injectJsonLd('schema-blog-article-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-blog-article-breadcrumb');
    };
  }, [article]);

  // Convert human-readable date to ISO format for schema
  const isoDate = article?.date
    ? (() => {
        const d = new Date(article.date);
        return isNaN(d.getTime()) ? article.date : d.toISOString().split('T')[0];
      })()
    : undefined;

  useArticleSchema(article ? {
    title: article.title,
    excerpt: article.excerpt,
    heroImage: article.heroImage,
    date: isoDate || article.date,
    author: article.author,
    slug: article.slug,
    readTime: article.readTime,
    category: article.category,
  } : null);

  useSEOHead({
    title: article ? `${article.title} | Rockhound Auto Blog` : 'Article Not Found',
    description: article?.excerpt || 'Read expert car buying advice and auto industry insights from Rockhound Auto Sales.',
    keywords: article?.category ? `${article.category}, car buying tips, auto advice, used car guides` : 'car buying tips, auto advice',
    canonicalUrl: `https://rockhoundauto.com/blog/${slug}`,
    ogType: 'article',
    ogImage: article?.heroImage || 'https://public.readdy.ai/ai/img_res/012e5d0ce41ca162c3cc7c55ff157c64.jpg',
    articlePublishedTime: isoDate,
    articleModifiedTime: isoDate,
    articleAuthor: article?.author,
  });

  // Inject FAQPage schema if article has FAQs
  useEffect(() => {
    if (!article || !(article as any).faqs || (article as any).faqs.length === 0) {
      return;
    }

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: (article as any).faqs.map((faq: { question: string; answer: string }) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    };

    injectJsonLd('schema-blog-article-faq', faqSchema);

    return () => {
      removeJsonLd('schema-blog-article-faq');
    };
  }, [article]);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const isCityBrokerGuide = article.category === 'City Broker Guide';
  const relatedArticles = blogPosts
    .filter(post => post.slug !== article.slug)
    .filter(post => {
      if (isCityBrokerGuide) {
        return post.category === 'City Broker Guide';
      } else {
        return post.category !== 'City Broker Guide' && (
          post.category === article.category ||
          post.tags.some(tag => article.tags.includes(tag))
        );
      }
    })
    .slice(0, isCityBrokerGuide ? 4 : 3);

  const cityGuideSuggestions = !isCityBrokerGuide
    ? blogPosts
        .filter(post => post.category === 'City Broker Guide')
        .slice(0, 2)
    : [];

  return (
    <main>
      <ArticleContent />

      <ArticleCta category={article.category} articleSlug={article.slug} />

      {(relatedArticles.length > 0 || cityGuideSuggestions.length > 0) && (
        <section className="py-10 sm:py-16 bg-[#F7F5F0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="font-['Oswald'] text-2xl sm:text-3xl md:text-4xl font-bold text-[#1C2B1E] mb-3 sm:mb-4">
                {isCityBrokerGuide ? 'More City Broker Guides' : 'Related Articles'}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                {isCityBrokerGuide
                  ? 'Discover how buyers in other cities are saving thousands with auto broker services'
                  : 'Continue learning about smart car buying strategies and insider tips'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {relatedArticles.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="bg-white rounded-xl border border-[#E8E2D6] overflow-hidden hover:border-[#E8B84B] transition-all group"
                >
                  <div className="h-40 sm:h-48 bg-gradient-to-br from-[#1C2B1E] to-[#2d4430] relative overflow-hidden">
                    <img
                      src={post.heroImage || post.image}
                      alt={`${post.title} — ${post.category} article featured image`}
                      width={600}
                      height={192}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                    />
                    <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                      <span className="inline-block px-2.5 py-1 bg-[#E8B84B] text-[#1C2B1E] text-xs font-semibold rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <h3 className="font-['Oswald'] text-lg sm:text-xl font-bold text-[#1C2B1E] mb-2 sm:mb-3 group-hover:text-[#E8B84B] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{post.readTime}</span>
                      <span className="flex items-center gap-1 text-[#E8B84B] font-medium group-hover:gap-2 transition-all">
                        Read More
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {!isCityBrokerGuide && cityGuideSuggestions.length > 0 && (
              <div className="mt-8 sm:mt-12 pt-8 sm:pt-12 border-t border-[#E8E2D6]">
                <div className="text-center mb-6 sm:mb-8">
                  <h3 className="font-['Oswald'] text-xl sm:text-2xl font-bold text-[#1C2B1E] mb-2">
                    Looking for Local Broker Services?
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    See how auto brokers serve buyers in your area
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
                  {cityGuideSuggestions.map((post) => {
                    const cityMatch = post.title.match(/Auto Broker (.+?)(?:\s+—|\s+:)/);
                    const cityName = cityMatch ? cityMatch[1] : post.title;

                    return (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        className="bg-gradient-to-br from-[#1C2B1E] to-[#2d4430] rounded-xl p-5 sm:p-6 border border-[#E8E2D6] hover:border-[#E8B84B] transition-all group"
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#E8B84B] rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-map-pin-2-fill text-[#1C2B1E] text-lg sm:text-xl"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-['Oswald'] text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2 group-hover:text-[#E8B84B] transition-colors">
                              {cityName}
                            </h4>
                            <p className="text-xs sm:text-sm text-white/80 mb-2 sm:mb-3 line-clamp-2">
                              {post.excerpt}
                            </p>
                            <span className="inline-flex items-center gap-1 text-[#E8B84B] text-sm font-medium group-hover:gap-2 transition-all">
                              Learn More
                              <i className="ri-arrow-right-line"></i>
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <YouMightAlsoLike currentSlug={article.slug} currentCategory={article.category} currentTags={article.tags} />

      <BlogNewsletter />
      <BackToTop />
    </main>
  );
}